import React, { useState, useEffect } from 'react';
import { Card, Spin, Table, Input, Button, Select, Drawer, Space } from 'antd';
import {
  DollarOutlined,
  ReloadOutlined,
  SearchOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import ReportService from '../../../services/ReportService';

const { Option } = Select;

const ProductCostSalesRatioReports = () => {
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [branches, setBranches] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [draftSelectedBranch, setDraftSelectedBranch] = useState(null);

  const [productData, setProductData] = useState([]);
  const [productDataLoading, setProductDataLoading] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const token = localStorage.getItem("token") || localStorage.getItem("x-access-token");
  const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
  const companyCode = userDetails?.CompanyCode || null;

  useEffect(() => {
    const loadBranches = async () => {
      if (!companyCode) {
        setBranches([]);
        return;
      }
      try {
        const response = await ReportService.company.getBranches({
          token,
          CompanyCode: companyCode
        });
        if (response.status === 200) {
          const data = Array.isArray(response.data) ? response.data : (response.data?.data || response.data || []);
          setBranches(data);
        }
      } catch (error) {
        console.error('Error loading branches:', error);
        setBranches([]);
      }
    };
    loadBranches();
  }, [token, companyCode]);

  const fetchProductData = async (branchCodeOverride = null) => {
    try {
      setProductDataLoading(true);

      if (!companyCode) {
        console.error('CompanyCode is required');
        setProductData([]);
        return;
      }

      const response = await ReportService.product.getProductCostSalesRatioReport({
        token,
        CompanyCode: companyCode,
        BranchCode: branchCodeOverride !== null && branchCodeOverride !== undefined ? branchCodeOverride : (selectedBranch || undefined)
      });

      if (response.status === 200) {
        const data = Array.isArray(response.data) ? response.data : (response.data?.data || response.data || []);
        setProductData(data);
      } else {
        setProductData([]);
      }
    } catch (error) {
      console.error('Error fetching product cost sales ratio:', error);
      setProductData([]);
    } finally {
      setProductDataLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await fetchProductData();
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const openFilters = () => {
    setDraftSelectedBranch(selectedBranch);
    setIsFilterOpen(true);
  };

  const applyFilters = () => {
    setSelectedBranch(draftSelectedBranch);
    setIsFilterOpen(false);
    fetchProductData(draftSelectedBranch || null);
  };

  const clearFilters = () => {
    const defaultSelectedBranch = null;
    setDraftSelectedBranch(defaultSelectedBranch);
    setSelectedBranch(defaultSelectedBranch);
    fetchProductData(null);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const productColumns = [
    {
      title: <span className={isMobile ? "text-sm" : "text-base"}>Ürün Adı</span>,
      dataIndex: 'UrunAdi',
      key: 'UrunAdi',
      width: isMobile ? 120 : undefined,
      fixed: isMobile ? 'left' : false,
      render: (text, record) => (
        <span className={isMobile ? "text-sm font-semibold" : "text-base font-semibold"}>
          {text}
          {record.PorsiyonAdi && <span className="text-gray-500 text-xs"> ({record.PorsiyonAdi})</span>}
        </span>
      ),
      ellipsis: true
    },
    {
      title: <span className={isMobile ? "text-sm" : "text-base"}>Maliyet</span>,
      dataIndex: 'Maliyet',
      key: 'Maliyet',
      width: isMobile ? 90 : undefined,
      align: 'right',
      render: (value) => (
        <span className={`${isMobile ? "text-sm" : "text-base"} font-semibold text-purple-600`}>
          {formatNumber(value || 0)} ₺
        </span>
      ),
      sorter: (a, b) => (a.Maliyet || 0) - (b.Maliyet || 0),
    },
    {
      title: <span className={isMobile ? "text-sm" : "text-base"}>Satış Fiyatı</span>,
      dataIndex: 'SatisFiyati',
      key: 'SatisFiyati',
      width: isMobile ? 90 : undefined,
      align: 'right',
      render: (value) => (
        <span className={`${isMobile ? "text-sm" : "text-base"} font-semibold text-blue-600`}>
          {formatNumber(value || 0)} ₺
        </span>
      ),
      sorter: (a, b) => (a.SatisFiyati || 0) - (b.SatisFiyati || 0),
    },
    {
      title: <span className={isMobile ? "text-sm" : "text-base"}>Kar/Zarar Tutarı</span>,
      dataIndex: 'Kar',
      key: 'Kar',
      width: isMobile ? 100 : undefined,
      align: 'right',
      render: (value) => {
        const kar = parseFloat(value || 0);
        return (
          <span className={`${isMobile ? "text-sm" : "text-base"} font-semibold ${kar >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatNumber(kar)} ₺
          </span>
        );
      },
      sorter: (a, b) => (a.Kar || 0) - (b.Kar || 0),
    },
    {
      title: <span className={isMobile ? "text-sm" : "text-base"}>Kar/Zarar Oranı</span>,
      dataIndex: 'KarYuzdesi',
      key: 'KarYuzdesi',
      width: isMobile ? 100 : undefined,
      align: 'right',
      render: (value) => {
        const karYuzdesi = parseFloat(value || 0);
        return (
          <span className={`${isMobile ? "text-sm" : "text-base"} font-semibold ${karYuzdesi >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatNumber(karYuzdesi)}%
          </span>
        );
      },
      sorter: (a, b) => (a.KarYuzdesi || 0) - (b.KarYuzdesi || 0),
    },
  ];

  const filterData = (data) => {
    if (!searchText) return data;
    const needle = searchText.toLowerCase();
    return data.filter(row =>
      String(row.UrunAdi || '').toLowerCase().includes(needle) ||
      String(row.PorsiyonAdi || '').toLowerCase().includes(needle) ||
      String(row.SubeAdi || '').toLowerCase().includes(needle) ||
      String(row.SubeKodu || '').toLowerCase().includes(needle)
    );
  };

  const groupedData = productData.reduce((acc, item) => {
    const key = item.SubeKodu || 'unknown';
    if (!acc[key]) {
      acc[key] = {
        SubeKodu: item.SubeKodu,
        SubeAdi: item.SubeAdi || 'Bilinmeyen Şube',
        products: []
      };
    }
    acc[key].products.push(item);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-50 via-white to-slate-50 border border-slate-200/60 shadow-lg backdrop-blur-sm mb-4">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-indigo-500/5 to-purple-500/5"></div>
        <div className="relative px-3 py-2 sm:px-4 sm:py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg sm:rounded-xl shadow-lg">
              <DollarOutlined className="text-white text-lg sm:text-xl" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl lg:text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent leading-tight">
                Ürünlerin Maliyeti
              </h1>
              <p className="text-slate-500 text-xs sm:text-sm font-medium">
                Ürün bazlı maliyet ve satış fiyatı karşılaştırması
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchProductData(selectedBranch || null)}
              loading={productDataLoading}
            >
              Yenile
            </Button>
            <Button icon={<FilterOutlined />} onClick={openFilters}>Filtreler</Button>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4 flex flex-col sm:flex-row gap-3">
        <Input
          allowClear
          placeholder="Ürün veya porsiyon ara..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          size="large"
          className="flex-1 rounded-lg"
        />
      </div>

      {/* Product Cost Sales Ratio Report */}
      {Object.values(groupedData).map((branch) => {
        const filteredProducts = filterData(branch.products);

        return (
          <Card
            key={branch.SubeKodu || 'unknown'}
            title={
              <div className="flex items-center gap-2">
                <DollarOutlined className="text-blue-500" />
                <h2 className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold`}>
                  {branch.SubeAdi}
                </h2>
              </div>
            }
            bordered={false}
            className="shadow-lg rounded-2xl border border-gray-100 bg-white mb-4"
            styles={{ body: { padding: isMobile ? 12 : 18 } }}
          >
            <Table
              columns={productColumns}
              dataSource={filteredProducts}
              loading={productDataLoading}
              rowKey={(record) => `${record.UrunID}-${record.PorsiyonID}-${record.SubeKodu}`}
              pagination={{
                pageSize: isMobile ? 10 : 20,
                showSizeChanger: !isMobile,
                showTotal: (total) => `Toplam ${total} ürün`,
              }}
              size={isMobile ? 'small' : 'middle'}
              scroll={isMobile ? { x: 'max-content' } : undefined}
            />
          </Card>
        );
      })}

      <Drawer title="Filtreler" placement="right" open={isFilterOpen} onClose={() => setIsFilterOpen(false)} width={isMobile ? 280 : 360}>
        <Space direction="vertical" size="middle" className="w-full">
          <div className="flex flex-col gap-1">
            <span className="text-xs text-slate-500">Şube</span>
            <Select
              value={draftSelectedBranch}
              onChange={(value) => setDraftSelectedBranch(value)}
              placeholder="Şube seçiniz"
              className="w-full"
              allowClear
            >
              {branches.map((branch) => (
                <Select.Option key={branch.SubeKodu} value={branch.SubeKodu}>
                  {branch.SubeAdi}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div className="flex items-center justify-between gap-2 pt-2">
            <Button onClick={clearFilters}>Temizle</Button>
            <Button type="primary" danger ghost={false} onClick={applyFilters} className="!bg-rose-600 !border-rose-600 !text-white hover:!bg-rose-700 hover:!border-rose-700">Uygula</Button>
          </div>
        </Space>
      </Drawer>
    </div>
  );
};

export default ProductCostSalesRatioReports;

